/**
 * Générateur — Automatisme : critères de divisibilité par 2, 5 et 10
 * Attendu ID : 1039 | Niveau : 5ème | Domaine : Opérations
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Divisible par 2, 5 ou 10 ?          (V/F décisions)
 *   - Repérer le nombre divisible         (QCM + variante intrus « N'EST PAS »)
 *   - Le multiple dans l'encadrement      (saisie)
 *   - Divisibilité : toujours vrai ?      (V/F logique, cadence raisonnement)
 *
 * ── Plan de cartes validé (2026-08-30) ─────────────────────────────────────
 * Source : Automatisme n°1 du doc 5e — tout se lit sur le CHIFFRE DES UNITÉS,
 * jamais de division posée. LIMITE DE PÉRIMÈTRE : les critères par 3 et 9
 * sont un objectif de 5e, PAS un automatisme — ils n'apparaissent nulle part,
 * même en distracteur. INVARIANTS (amendements prof 2026-08-30) :
 *   - le statut de divisibilité est FIXÉ par le choix du chiffre des unités,
 *     puis RE-VÉRIFIÉ numériquement (n % d) — throw si contradiction : on
 *     préfère un générateur qui refuse d'émettre à un exercice faux ;
 *   - saisie : les bornes de l'encadrement ne sont JAMAIS multiples de 5
 *     (donc jamais du diviseur cible) → l'inclusivité des bornes est sans
 *     objet et l'unicité du multiple est recomptée (throw sinon) ;
 *   - QCM : exactement UN candidat au statut demandé (recompté, throw) ;
 *     familles de distracteurs nommées dans _meta.params.distracteurs ;
 *   - variante intrus : « N'EST PAS » en majuscules (pas de gras dans le
 *     pipeline d'énoncés) + contenu.negated = true (persisté, isole les
 *     stems négatifs dans les futures stats de remédiation) ;
 *   - V/F logique : pool FIXE à vérité fixe (rien à générer, rien à casser),
 *     contenu.cadence = 'raisonnement' (métadonnée DORMANTE, comme le flag
 *     calculatrice : aucun chrono par question n'existe encore côté élève) ;
 *   - couverture PAR CARTE (le gen est sans état, une série se compose côté
 *     app) : le V/F décisions couvre les trois diviseurs avec au moins un
 *     vrai et un faux, et toujours un cas vicieux (finit par 5 interrogé sur
 *     10, pair interrogé sur 5, finit par 0 interrogé sur 2...) ;
 *   - milliers : espace fine insécable U+202F à l'affichage, réponses de
 *     saisie en brut (la tolérance numérique du scoring fait le reste).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie,
} from '../../../utils';

const ATTENDU_ID = 1039;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DECISIONS = 'Divisible par 2, 5 ou 10 ?';
const L_REPERER = 'Repérer le nombre divisible';
const L_ENCADREMENT = 'Le multiple dans l\'encadrement';
const L_LOGIQUE = 'Divisibilité : toujours vrai ?';

// ─── Le domaine ────────────────────────────────────────────────────────────

/** Chiffres des unités par statut vis-à-vis de chaque diviseur. */
const UNITES = {
    2: { oui: [0, 2, 4, 6, 8], non: [1, 3, 5, 7, 9] },
    5: { oui: [0, 5], non: [1, 2, 3, 4, 6, 7, 8, 9] },
    10: { oui: [0], non: [1, 2, 3, 4, 5, 6, 7, 8, 9] },
};

/** Espace fine insécable des milliers (affichage uniquement). */
const fmtNb = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');

/**
 * AUTO-VÉRIFICATION : le statut réel de n vis-à-vis de d doit être celui
 * demandé à la construction. Filet contre toute évolution du constructeur.
 */
const verifierStatut = (n, d, divisible) => {
    if ((n % d === 0) !== divisible) {
        throw new Error(`gen_1039 : ${n} % ${d} contredit le statut construit (${divisible})`);
    }
    return n;
};

/**
 * Nombre de 3-4 chiffres au statut IMPOSÉ vis-à-vis de d : préfixe libre,
 * chiffre des unités tiré dans l'ensemble du statut (la vérité est fixée par
 * construction, puis re-vérifiée). `unite` force un chiffre précis (cas
 * vicieux) ; `exclure` évite les doublons d'un même tirage.
 */
const nombreStatut = (d, divisible, { unite = null, exclure = null, prefixe = [10, 999] } = {}) => {
    for (let essai = 0; essai < 50; essai++) {
        const u = unite !== null ? unite : randomChoice(UNITES[d][divisible ? 'oui' : 'non']);
        const n = randInt(prefixe[0], prefixe[1]) * 10 + u;
        if (exclure && exclure.has(n)) continue;
        return verifierStatut(n, d, divisible);
    }
    // Repli déterministe : préfixe fixe hors zone de collision probable.
    const u = unite !== null ? unite : UNITES[d][divisible ? 'oui' : 'non'][0];
    return verifierStatut((prefixe[1] - 13) * 10 + u, d, divisible);
};

/**
 * Gammes de longueur : au QCM, les QUATRE options d'un même tirage partagent
 * la même longueur d'écriture (3 ou 4 chiffres) : la taille du nombre ne
 * doit jamais devenir un indice (challenge élève 2026-08-30).
 */
const GAMMES = [
    { prefixe: [10, 99], dizaines: [1, 9] },      // nombres à 3 chiffres
    { prefixe: [100, 999], dizaines: [10, 99] },  // nombres à 4 chiffres
];

// ─── Format 1 : V/F décisions (le cœur de la série flash) ─────────────────

/**
 * Cas vicieux imposé à chaque tirage : chacun matérialise une confusion
 * réelle (le corrigé source les nomme). `unite` contraint le chiffre des
 * unités, `d` le diviseur interrogé, `divisible` la vérité qui en découle.
 */
const CAS_VICIEUX = [
    { d: 10, unite: () => 5, divisible: false },  // finit par 5 : par 5, PAS par 10
    { d: 5, unite: () => randInt(1, 4) * 2, divisible: false }, // pair : pas par 5 pour autant
    { d: 2, unite: () => 0, divisible: true },    // finit par 0 : pair aussi
    { d: 5, unite: () => 0, divisible: true },    // finit par 0 : par 5 aussi
    { d: 2, unite: () => 5, divisible: false },   // finit par 5 : impair
];

const genDecisionsVf = () => {
    const vus = new Set();
    const lignes = [];
    const bonnes = [];
    const pousser = (n, d, divisible) => {
        vus.add(n);
        lignes.push(`${fmtNb(n)} est divisible par ${d}`);
        bonnes.push(divisible ? 0 : 1);
    };

    // 1. Le cas vicieux du tirage (unité imposée, tirée à l'usage).
    const vice = randomChoice(CAS_VICIEUX);
    pousser(nombreStatut(vice.d, vice.divisible, { unite: vice.unite() }), vice.d, vice.divisible);

    // 2. Trois lignes couvrant les diviseurs restants (les trois diviseurs
    //    sont TOUJOURS présents sur la carte), statuts tirés puis corrigés
    //    pour garantir au moins un vrai ET un faux sur l'ensemble.
    const restants = shuffle([2, 5, 10].filter((d) => d !== vice.d));
    const statuts = [randomChoice([true, false]), randomChoice([true, false]), randomChoice([true, false])];
    const tous = [vice.divisible, ...statuts];
    if (tous.every((s) => s)) statuts[0] = false;
    if (tous.every((s) => !s)) statuts[0] = true;
    restants.forEach((d, i) => {
        pousser(nombreStatut(d, statuts[i], { exclure: vus }), d, statuts[i]);
    });
    const dDernier = randomChoice([2, 5, 10]);
    pousser(nombreStatut(dDernier, statuts[2], { exclure: vus }), dDernier, statuts[2]);

    // Mélange lignes/réponses d'un seul geste.
    const ordre = shuffle(lignes.map((_, i) => i));
    return makeVraiFaux(
        ordre.map((i) => lignes[i]),
        ordre.map((i) => bonnes[i]),
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L_DECISIONS),
    );
};

// ─── Format 2 : QCM discrimination (+ variante intrus) ────────────────────

/**
 * Contrat distracteurs (cf. REGLES_DISTRACTEURS_QCM en fin de fichier) :
 * pour chaque diviseur, trois familles NOMMÉES de faux candidats. Chaque
 * fabricant retourne un nombre NON divisible par d, dont l'écriture incarne
 * la confusion visée (re-vérifié non divisible par construction d'unités).
 */
const FAMILLES_DISTRACTEURS = {
    2: [
        // Impair dont un chiffre PAIR est visible aux dizaines : l'élève qui
        // « voit du pair » coche sans lire les unités.
        { code: 'contient_chiffre_pair', fabrique: (ex, g) => contientHorsUnites(2, randomChoice([2, 4, 6, 8]), ex, g) },
        { code: 'finit_par_5', fabrique: (ex, g) => nombreStatut(2, false, { unite: 5, exclure: ex, prefixe: g.prefixe }) },
        { code: 'impair_quelconque', fabrique: (ex, g) => nombreStatut(2, false, { exclure: ex, prefixe: g.prefixe }) },
    ],
    5: [
        { code: 'contient_5_hors_unites', fabrique: (ex, g) => contientHorsUnites(5, 5, ex, g) },
        { code: 'pair_pris_pour_multiple', fabrique: (ex, g) => nombreStatut(5, false, { unite: randInt(1, 4) * 2, exclure: ex, prefixe: g.prefixe }) },
        { code: 'unites_quelconques', fabrique: (ex, g) => nombreStatut(5, false, { unite: randomChoice([1, 3, 7, 9]), exclure: ex, prefixe: g.prefixe }) },
    ],
    10: [
        { code: 'finit_par_5_confusion_5_10', fabrique: (ex, g) => nombreStatut(10, false, { unite: 5, exclure: ex, prefixe: g.prefixe }) },
        { code: 'pair_pris_pour_multiple', fabrique: (ex, g) => nombreStatut(10, false, { unite: randInt(1, 4) * 2, exclure: ex, prefixe: g.prefixe }) },
        { code: 'contient_0_hors_unites', fabrique: (ex, g) => contientHorsUnites(10, 0, ex, g) },
    ],
};

/**
 * Nombre NON divisible par d dont le chiffre `c` apparaît AILLEURS qu'aux
 * unités (la misconception « le nombre contient le chiffre ») : dizaines = c,
 * unités tirées non divisibles, longueur imposée par la gamme.
 */
const contientHorsUnites = (d, c, exclure, gamme = GAMMES[1]) => {
    for (let essai = 0; essai < 50; essai++) {
        const u = randomChoice(UNITES[d].non.filter((x) => x !== c));
        const n = (randInt(gamme.dizaines[0], gamme.dizaines[1]) * 10 + c) * 10 + u;
        if (exclure && exclure.has(n)) continue;
        return verifierStatut(n, d, false);
    }
    return verifierStatut((gamme.dizaines[1] * 10 + c) * 10 + UNITES[d].non.filter((x) => x !== c)[0], d, false);
};

/** Recompte du contrat QCM : exactement `attendu` candidats divisibles. */
const verifierComptage = (valeurs, d, attendu) => {
    const nb = valeurs.filter((v) => v % d === 0).length;
    if (nb !== attendu) {
        throw new Error(`gen_1039 : QCM d=${d}, ${nb} divisibles au lieu de ${attendu}`);
    }
};

const genRepererQcm = () => {
    const d = randomChoice([2, 5, 10]);
    // Une seule gamme de longueur pour les 4 options du tirage : la taille
    // du nombre ne doit jamais trahir la réponse.
    const gamme = randomChoice(GAMMES);
    const vus = new Set();
    const bon = nombreStatut(d, true, { exclure: vus, prefixe: gamme.prefixe });
    vus.add(bon);
    const distracteurs = FAMILLES_DISTRACTEURS[d].map(({ code, fabrique }) => {
        const n = fabrique(vus, gamme);
        vus.add(n);
        return { valeur: n, code };
    });
    const valeurs = [bon, ...distracteurs.map((x) => x.valeur)];
    verifierComptage(valeurs, d, 1);

    const payload = makeQcm(
        valeurs.map(fmtNb),
        fmtNb(bon),
        `<p><b>Quel nombre est divisible par ${d} ?</b></p>`,
        opts(L_REPERER),
        false,
    );
    payload._meta.params = { d, reponse: bon, distracteurs };
    return payload;
};

const genRepererQcmNeg = () => {
    const d = randomChoice([2, 5, 10]);
    const gamme = randomChoice(GAMMES);
    const vus = new Set();
    // L'intrus porte la famille la plus riche du diviseur (première du
    // contrat) : « contient le chiffre » pour 2 et 5 (chiffre pair visible,
    // 5 hors unités), « finit par 5 » pour 10 (la confusion 5/10).
    const intrusFamille = FAMILLES_DISTRACTEURS[d][0];
    const intrus = intrusFamille.fabrique(vus, gamme);
    vus.add(intrus);
    // Trois nombres divisibles, unités variées quand le critère le permet.
    const divisibles = [];
    for (let i = 0; i < 3; i++) {
        const n = nombreStatut(d, true, { exclure: vus, prefixe: gamme.prefixe });
        vus.add(n);
        divisibles.push(n);
    }
    const valeurs = [intrus, ...divisibles];
    verifierComptage(valeurs, d, 3);

    const payload = makeQcm(
        valeurs.map(fmtNb),
        fmtNb(intrus),
        `<p><b>Quel nombre N'EST PAS divisible par ${d} ?</b></p>`,
        opts(L_REPERER),
        false,
    );
    // Stem négatif signalé pour les stats de remédiation (persisté).
    payload.contenu = { ...payload.contenu, negated: true };
    payload._meta.params = { d, reponse: intrus, negated: true, familleIntrus: intrusFamille.code };
    return payload;
};

// ─── Format 3 : saisie, le multiple dans l'encadrement ────────────────────

/**
 * Le multiple m est tiré d'abord, l'encadrement construit AUTOUR : bornes
 * jamais multiples de 5 (amendement prof : ni le diviseur cible ni 5 quand
 * la cible est 10 → l'inclusivité des bornes est sans objet), largeur qui ne
 * laisse place qu'à UN multiple (re-compté, throw sinon).
 */
const genEncadrementSaisie = () => {
    const cible = randomChoice([10, 10, 5]); // le 10 de la source domine
    const m = cible === 10 ? randInt(10, 998) * 10 : randInt(21, 1996) * 5;
    // Écarts : jamais 5 (borne multiple de 5), jamais 0, ≤ 9 pour la cible
    // 10 (le multiple voisin est à ±10), ≤ 2 pour la cible 5 (voisin à ±5).
    const ecarts = cible === 10 ? [1, 2, 3, 4, 6, 7, 8, 9] : [1, 2];
    const a = m - randomChoice(ecarts);
    const b = m + randomChoice(ecarts);

    // AUTO-VÉRIFICATION : bornes hors multiples de 5, un seul multiple dans
    // l'encadrement (bornes comprises : elles ne peuvent pas l'être).
    if (a % 5 === 0 || b % 5 === 0) {
        throw new Error(`gen_1039 : borne multiple de 5 (${a}, ${b})`);
    }
    let compte = 0;
    for (let k = a; k <= b; k++) if (k % cible === 0) compte++;
    if (compte !== 1) {
        throw new Error(`gen_1039 : ${compte} multiples de ${cible} entre ${a} et ${b}`);
    }

    const payload = makeSaisie(
        String(m),
        `<p><b>Donne le multiple de ${cible} compris entre ${fmtNb(a)} et ${fmtNb(b)}.</b></p>`,
        opts(L_ENCADREMENT),
    );
    payload._meta.params = { cible, a, b, reponse: m };
    return payload;
};

// ─── Format 4 : V/F logique des critères ──────────────────────────────────

/**
 * Pool FIXE à vérité fixe : rien à générer, rien à casser. `cle` regroupe
 * les affirmations d'une même idée (jamais deux fois la même idée dans un
 * tirage). Le contre-exemple vit dans le corrigé du prof, pas dans l'énoncé.
 */
const POOL_LOGIQUE = [
    { cle: '10vers5', texte: 'Tout nombre divisible par 10 est aussi divisible par 5.', vrai: true },
    { cle: '10vers2', texte: 'Tout nombre divisible par 10 est aussi divisible par 2.', vrai: true },
    { cle: '5vers10', texte: 'Tout nombre divisible par 5 est aussi divisible par 10.', vrai: false },
    { cle: '2vers10', texte: 'Tout nombre divisible par 2 est aussi divisible par 10.', vrai: false },
    { cle: '2et5vers10', texte: 'Un nombre divisible par 2 et par 5 est toujours divisible par 10.', vrai: true },
    { cle: 'zero_final', texte: 'Un nombre qui se termine par 0 est divisible par 2, par 5 et par 10.', vrai: true },
    { cle: 'contient5', texte: 'Un nombre qui contient le chiffre 5 est toujours divisible par 5.', vrai: false },
    { cle: 'finit5', texte: 'Un nombre qui se termine par 5 est divisible par 5.', vrai: true },
    { cle: 'impair5', texte: 'Un nombre impair peut être divisible par 5.', vrai: true },
    { cle: 'impair10', texte: 'Un nombre impair peut être divisible par 10.', vrai: false },
];

const genLogiqueVf = () => {
    // 3 affirmations de clés distinctes, au moins une vraie et une fausse.
    let tirage = shuffle(POOL_LOGIQUE).slice(0, 3);
    for (let essai = 0; essai < 30; essai++) {
        const vrais = tirage.filter((x) => x.vrai).length;
        if (vrais >= 1 && vrais <= 2) break;
        tirage = shuffle(POOL_LOGIQUE).slice(0, 3);
    }
    // Repli déterministe : si le hasard s'obstine en mono-vérité, la 3e
    // ligne est remplacée par une affirmation de l'autre vérité.
    if (tirage.every((x) => x.vrai) || tirage.every((x) => !x.vrai)) {
        const manque = !tirage[0].vrai;
        tirage[2] = POOL_LOGIQUE.find((x) => x.vrai === manque && !tirage.includes(x));
    }
    const payload = makeVraiFaux(
        tirage.map((x) => x.texte),
        tirage.map((x) => (x.vrai ? 0 : 1)),
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L_LOGIQUE),
    );
    // Carte de raisonnement, pas de récupération flash : métadonnée DORMANTE
    // (aucun chrono par question côté élève aujourd'hui, cf. plan de cartes).
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
    payload._meta.params = { cles: tirage.map((x) => x.cle) };
    return payload;
};

// ─── Contrat distracteurs (référence de revue, format QCM) ────────────────

/**
 * Chaque erreur est une MISCONCEPTION NOMMÉE d'élève, jamais du bruit.
 * Arbitrage prof (décideur didactique), 2026-08-30.
 */
export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'exactement UN candidat au statut demandé, recompté après construction (throw sinon)',
        'le statut de chaque option est fixé par son chiffre des unités, jamais vérifié a posteriori seulement',
        'les critères par 3 et 9 sont HORS PÉRIMÈTRE (objectif de 5e, pas un automatisme) : aucune option n\'y fait appel',
        'chaque distracteur porte son code dans _meta.params.distracteurs (futur feedback pédagogique nommant l\'erreur)',
    ],
    reperer_qcm: {
        support: 'quatre nombres de 3-4 chiffres, un seul divisible par d ∈ {2, 5, 10}',
        familles: {
            contient_le_chiffre: 'le chiffre du diviseur (5 ou 0) apparaît HORS unités : l\'élève repère le chiffre au lieu de lire les unités — LA misconception de l\'attendu',
            confusion_2_5_10: 'pair pris pour multiple de 5 ou 10, ou terminé par 5 pris pour multiple de 10',
            unites_quelconques: 'unités hors critère, sans piège : le filet bas de la discrimination',
        },
    },
    reperer_qcm_neg: {
        support: 'trois divisibles + un intrus (la bonne réponse), stem « N\'EST PAS » en majuscules + contenu.negated',
        note: 'l\'intrus porte la famille la plus riche du diviseur (contient_le_chiffre pour 5 et 10)',
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    decisions_vf: genDecisionsVf,
    reperer_qcm: genRepererQcm,
    reperer_qcm_neg: genRepererQcmNeg,
    encadrement_saisie: genEncadrementSaisie,
    logique_vf: genLogiqueVf,
};

const FORMAT_LABELS = {
    vf: 'Vrai/Faux', qcm: 'QCM', qcm_neg: 'QCM intrus', saisie: 'Saisie',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'decisions', label: L_DECISIONS, formatsList: buildFormats('decisions') },
    { id: 'reperer', label: L_REPERER, formatsList: buildFormats('reperer') },
    { id: 'encadrement', label: L_ENCADREMENT, formatsList: buildFormats('encadrement') },
    { id: 'logique', label: L_LOGIQUE, formatsList: buildFormats('logique') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
